import type { ReactElement } from 'react';
import {
  Heart,
  MessageCircle,
  Repeat2,
  Share,
  Bookmark,
  MoreHorizontal,
  Play,
  ThumbsUp,
  Eye,
} from 'lucide-react';
import type { CoverageItem, SocialPlatform } from '@/client/types/media';
import { PLATFORM_LABEL, SOCIAL_FORMAT_LABEL, publicMetrics } from '@/client/types/media';
import { formatCompact } from '../fixtures';
import { PlatformLogoBadge } from './PlatformLogo';

function m(item: CoverageItem) {
  return publicMetrics(item);
}

function PlatformChrome({
  platform,
  subtitle,
}: {
  platform: SocialPlatform;
  subtitle?: string;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
      <PlatformLogoBadge platform={platform} size="sm" />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-gray-800">{PLATFORM_LABEL[platform]}</p>
        {subtitle && (
          <p className="truncate text-[10px] text-gray-500">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function initials(name: string) {
  return name
    .replace(/@/g, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-xs font-semibold text-white ${className ?? ''}`}
    >
      {initials(name)}
    </div>
  );
}

function MediaSurface({
  tone,
  label,
  tall,
}: {
  tone?: string;
  label?: string;
  tall?: boolean;
}) {
  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${
        tall ? 'aspect-[9/14]' : 'aspect-video'
      } ${tone ?? 'bg-gradient-to-br from-gray-200 to-gray-400'}`}
    >
      <Play className="h-10 w-10 text-white/80 drop-shadow" />
      {label && (
        <span className="absolute bottom-2 right-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {label}
        </span>
      )}
    </div>
  );
}

function TrackingStrip({ item }: { item: CoverageItem }) {
  const s = item.social;
  const er = m(item).engagementRate;
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-[11px] text-gray-600 sm:grid-cols-4">
      <div>
        <p className="text-gray-400">Impressions</p>
        <p className="font-semibold text-gray-900">
          {formatCompact(m(item).impressions ?? m(item).views ?? m(item).reach)}
        </p>
      </div>
      <div>
        <p className="text-gray-400">Eng. rate</p>
        <p className="font-semibold text-gray-900">{er != null ? `${(er * 100).toFixed(1)}%` : '—'}</p>
      </div>
      <div>
        <p className="text-gray-400">Format</p>
        <p className="font-semibold text-gray-900">
          {s ? SOCIAL_FORMAT_LABEL[s.format] : '—'}
        </p>
      </div>
      <div>
        <p className="text-gray-400">Tracked</p>
        <p className="font-semibold text-gray-900">
          {s?.trackedSince ? new Date(s.trackedSince).toLocaleDateString() : '—'}
        </p>
      </div>
    </div>
  );
}

function XPreview({ item }: { item: CoverageItem }) {
  const s = item.social!;
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <PlatformChrome platform="x" subtitle={SOCIAL_FORMAT_LABEL[s.format]} />
      <div className="p-4">
        <div className="flex gap-3">
          <Avatar name={s.authorDisplayName} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 text-sm">
              <span className="font-bold text-gray-900">{s.authorDisplayName}</span>
              {s.authorVerified && <span className="text-sky-500">✓</span>}
              <span className="truncate text-gray-500">{s.authorHandle}</span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-400">{new Date(item.publishedAt).toLocaleDateString()}</span>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-[15px] leading-snug text-gray-900">
              {s.caption ?? item.bodyExcerpt ?? item.summary}
            </p>
            {(s.format === 'image' || s.format === 'video' || s.format === 'thread') &&
              s.mediaTone && (
                <div className="mt-3 overflow-hidden rounded-2xl border border-gray-200">
                  <MediaSurface tone={s.mediaTone} label={s.mediaLabel} />
                </div>
              )}
            <div className="mt-3 flex max-w-md justify-between text-gray-500">
              <span className="inline-flex items-center gap-1 text-xs">
                <MessageCircle className="h-4 w-4" /> {formatCompact(m(item).replies ?? m(item).comments)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs">
                <Repeat2 className="h-4 w-4" /> {formatCompact(m(item).shares)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs">
                <Heart className="h-4 w-4" /> {formatCompact(m(item).likes)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs">
                <Eye className="h-4 w-4" /> {formatCompact(m(item).impressions ?? m(item).reach)}
              </span>
            </div>
          </div>
        </div>
        <TrackingStrip item={item} />
      </div>
    </div>
  );
}

function InstagramPreview({ item }: { item: CoverageItem }) {
  const s = item.social!;
  const tall = s.format === 'reel';
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <PlatformChrome platform="instagram" subtitle={SOCIAL_FORMAT_LABEL[s.format]} />
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-[2px]">
            <Avatar name={s.authorDisplayName} className="h-8 w-8 border-2 border-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {s.authorHandle.replace(/^@/, '')}
              {s.authorVerified ? ' ✓' : ''}
            </p>
            {s.location && <p className="text-[10px] text-gray-500">{s.location}</p>}
          </div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-gray-500" />
      </div>
      <MediaSurface tone={s.mediaTone} label={s.mediaLabel} tall={tall} />
      <div className="space-y-2 px-3 py-3">
        <div className="flex items-center justify-between text-gray-900">
          <div className="flex gap-3">
            <Heart className="h-5 w-5" />
            <MessageCircle className="h-5 w-5" />
            <Share className="h-5 w-5" />
          </div>
          <Bookmark className="h-5 w-5" />
        </div>
        <p className="text-sm font-semibold">{formatCompact(m(item).likes)} likes</p>
        <p className="text-sm text-gray-900">
          <span className="font-semibold">{s.authorHandle.replace(/^@/, '')}</span>{' '}
          {s.caption ?? item.summary}
        </p>
        {s.hashtags && s.hashtags.length > 0 && (
          <p className="text-sm text-sky-700">{s.hashtags.map((h) => `#${h.replace(/^#/, '')}`).join(' ')}</p>
        )}
        <TrackingStrip item={item} />
      </div>
    </div>
  );
}

function LinkedInPreview({ item }: { item: CoverageItem }) {
  const s = item.social!;
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <PlatformChrome platform="linkedin" subtitle={SOCIAL_FORMAT_LABEL[s.format]} />
      <div className="p-4">
        <div className="flex gap-3">
          <Avatar name={s.authorDisplayName} className="rounded-md" />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {s.authorDisplayName}
              {s.authorVerified ? ' · ✓' : ''}
            </p>
            <p className="text-xs text-gray-500">
              {formatCompact(s.authorFollowers)} followers ·{' '}
              {new Date(item.publishedAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-900">
          {s.caption ?? item.summary}
        </p>
        {s.mediaTone && (
          <div className="mt-3 overflow-hidden rounded border border-gray-200">
            <MediaSurface tone={s.mediaTone} label={s.mediaLabel} />
          </div>
        )}
        <div className="mt-3 flex gap-4 border-t border-gray-100 pt-3 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <ThumbsUp className="h-3.5 w-3.5 text-[#0A66C2]" /> {formatCompact(m(item).likes)}
          </span>
          <span>{formatCompact(m(item).comments)} comments</span>
          <span>{formatCompact(m(item).shares)} reposts</span>
        </div>
        <TrackingStrip item={item} />
      </div>
    </div>
  );
}

function FacebookPreview({ item }: { item: CoverageItem }) {
  const s = item.social!;
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <PlatformChrome platform="facebook" subtitle={SOCIAL_FORMAT_LABEL[s.format]} />
      <div className="p-3">
        <div className="flex items-center gap-2">
          <Avatar name={s.authorDisplayName} />
          <div>
            <p className="text-sm font-semibold text-gray-900">{s.authorDisplayName}</p>
            <p className="text-[11px] text-gray-500">
              {new Date(item.publishedAt).toLocaleString()} · Public
            </p>
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-900">{s.caption ?? item.summary}</p>
        {s.quotedText && (
          <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
            Shared post: “{s.quotedText}”
          </div>
        )}
        {s.mediaTone && (
          <div className="mt-2 overflow-hidden">
            <MediaSurface tone={s.mediaTone} label={s.mediaLabel} />
          </div>
        )}
        <div className="mt-2 flex justify-between border-t border-gray-100 pt-2 text-xs text-gray-500">
          <span>👍 {formatCompact(m(item).likes)}</span>
          <span>
            {formatCompact(m(item).comments)} comments · {formatCompact(m(item).shares)} shares
          </span>
        </div>
        <TrackingStrip item={item} />
      </div>
    </div>
  );
}

function TikTokPreview({ item }: { item: CoverageItem }) {
  const s = item.social!;
  return (
    <div className="overflow-hidden rounded-xl border border-gray-800 bg-black text-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <PlatformLogoBadge platform="tiktok" size="sm" />
        <div>
          <p className="text-[11px] font-semibold text-white">TikTok</p>
          <p className="text-[10px] text-white/50">{SOCIAL_FORMAT_LABEL[s.format]}</p>
        </div>
      </div>
      <div className="relative">
        <MediaSurface tone={s.mediaTone} label={s.mediaLabel} tall />
        <div className="absolute bottom-3 left-3 right-14 space-y-1">
          <p className="text-sm font-semibold">{s.authorHandle}</p>
          <p className="line-clamp-3 text-xs text-white/90">{s.caption ?? item.summary}</p>
          {s.hashtags && (
            <p className="text-xs text-white/70">
              {s.hashtags.map((h) => `#${h.replace(/^#/, '')}`).join(' ')}
            </p>
          )}
        </div>
        <div className="absolute bottom-8 right-3 flex flex-col items-center gap-4 text-xs">
          <div className="flex flex-col items-center gap-1">
            <Heart className="h-6 w-6" />
            {formatCompact(m(item).likes)}
          </div>
          <div className="flex flex-col items-center gap-1">
            <MessageCircle className="h-6 w-6" />
            {formatCompact(m(item).comments)}
          </div>
          <div className="flex flex-col items-center gap-1">
            <Share className="h-6 w-6" />
            {formatCompact(m(item).shares)}
          </div>
        </div>
      </div>
      <div className="bg-black px-3 pb-3">
        <TrackingStrip item={item} />
      </div>
    </div>
  );
}

function YouTubePreview({ item }: { item: CoverageItem }) {
  const s = item.social!;
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <PlatformChrome platform="youtube" subtitle={SOCIAL_FORMAT_LABEL[s.format]} />
      <div className="relative">
        <MediaSurface tone={s.mediaTone} label={s.mediaLabel} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-12 w-16 items-center justify-center rounded-xl bg-red-600/90">
            <Play className="h-6 w-6 fill-white text-white" />
          </div>
        </div>
      </div>
      <div className="space-y-2 p-3">
        <p className="line-clamp-2 text-sm font-semibold text-gray-900">{item.title}</p>
        <div className="flex items-center gap-2">
          <Avatar name={s.authorDisplayName} className="h-7 w-7" />
          <div>
            <p className="text-xs font-medium text-gray-800">{s.authorDisplayName}</p>
            <p className="text-[11px] text-gray-500">
              {formatCompact(m(item).views)} views · {new Date(item.publishedAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <p className="line-clamp-2 text-xs text-gray-600">{s.caption}</p>
        <TrackingStrip item={item} />
      </div>
    </div>
  );
}

const PREVIEWS: Partial<Record<SocialPlatform, (item: CoverageItem) => ReactElement>> = {
  x: (item) => <XPreview item={item} />,
  instagram: (item) => <InstagramPreview item={item} />,
  linkedin: (item) => <LinkedInPreview item={item} />,
  facebook: (item) => <FacebookPreview item={item} />,
  tiktok: (item) => <TikTokPreview item={item} />,
  youtube: (item) => <YouTubePreview item={item} />,
};

interface SocialPlatformPreviewProps {
  item: CoverageItem;
  compact?: boolean;
}

/** Platform-styled mock preview for social coverage items. */
export function SocialPlatformPreview({ item }: SocialPlatformPreviewProps) {
  if (item.channel !== 'social' || !item.platform || !item.social) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
        No social preview available
      </div>
    );
  }

  const render = PREVIEWS[item.platform];
  if (!render) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700">
        <p className="font-medium">{PLATFORM_LABEL[item.platform]}</p>
        <p className="mt-1 text-gray-600">{item.social.caption ?? item.summary}</p>
      </div>
    );
  }

  return render(item);
}
